import {
  CHARACTER_ROLE_META,
  ORG_ROLE_META,
  PROJECT_FORMAT_META,
  PROJECT_ROLE_META,
  PROJECT_STATUS_META,
  PRODUCTION_STAGE_META,
  PRODUCTION_STATUS_META,
  REVISION_COLORS,
  STRUCTURE_STATUS_META,
  initials,
  type CharacterRole,
  type Localized,
  type OrgRole,
  type ProjectFormat,
  type ProjectPresenceUser,
  type ProjectRole,
  type ProjectStatus,
  type ProductionStage,
  type ProductionStatus,
  type RevisionColor,
  type StructureStatus,
} from "@korin/core";
import clsx, { type ClassValue } from "clsx";
import { getLocale, loc, localeTag, t } from "../i18n";

export function cn(...values: ClassValue[]): string {
  return clsx(values);
}

export { initials };

export function formatNumber(value: number): string {
  return new Intl.NumberFormat(localeTag()).format(value);
}

export function formatCompact(value: number): string {
  return new Intl.NumberFormat(localeTag(), { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

export function formatDate(value?: string | null, opts?: Intl.DateTimeFormatOptions): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(localeTag(), opts ?? { day: "numeric", month: "short", year: "numeric" }).format(date);
}

export function formatDateTime(value?: string | null): string {
  return formatDate(value, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatWords(n: number): string {
  if (getLocale() === "en") return `${formatNumber(n)} ${n === 1 ? "word" : "words"}`;
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  const word = abs > 10 && abs < 20 ? "слов" : last > 1 && last < 5 ? "слова" : last === 1 ? "слово" : "слов";
  return `${formatNumber(n)} ${word}`;
}

export function formatPages(n: number): string {
  if (getLocale() === "en") return `${formatNumber(n)} ${n === 1 ? "page" : "pages"}`;
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  const word = abs > 10 && abs < 20 ? "страниц" : last > 1 && last < 5 ? "страницы" : last === 1 ? "страница" : "страниц";
  return `${formatNumber(n)} ${word}`;
}

export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export function formatMinutes(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (getLocale() === "en") return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  return `${minutes} ${plural(minutes, "минута", "минуты", "минут")}`;
}

export function relativeTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value).getTime();
  if (Number.isNaN(date)) return "—";
  const diff = Date.now() - date;
  const minutes = Math.round(diff / 60000);
  if (getLocale() === "en") {
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days} ${days === 1 ? "day" : "days"} ago`;
    return formatDate(value);
  }
  if (minutes < 1) return "только что";
  if (minutes < 60) return `${minutes} ${plural(minutes, "минуту", "минуты", "минут")} назад`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${plural(hours, "час", "часа", "часов")} назад`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} ${plural(days, "день", "дня", "дней")} назад`;
  return formatDate(value);
}

function localized(label?: Localized | null): string | undefined {
  if (!label) return undefined;
  return loc(label);
}

export function roleLabel(role?: ProjectRole | string | null): string {
  if (!role) return t("Участник");
  return localized(PROJECT_ROLE_META[role as ProjectRole]?.label) ?? role;
}

export function orgRoleLabel(role?: OrgRole | string | null): string {
  if (!role) return t("Сотрудник");
  return localized(ORG_ROLE_META[role as OrgRole]?.label) ?? role;
}

const WORKSPACE_ROLE_LABEL: Record<string, Localized> = {
  owner: { ru: "Владелец", en: "Owner" },
  admin: { ru: "Администратор", en: "Admin" },
  member: { ru: "Участник", en: "Member" },
  viewer: { ru: "Наблюдатель", en: "Viewer" },
};

export function workspaceRoleLabel(role?: string | null): string {
  if (!role) return t("Участник");
  return localized(WORKSPACE_ROLE_LABEL[role]) ?? role;
}

export function voiceProfileStatusLabel(status?: string | null): string {
  if (status === "ready") return t("Готов");
  if (status === "processing") return t("Готовится");
  if (status === "failed") return t("Ошибка");
  return status ?? "—";
}

export function roleColor(role?: ProjectRole | string | null): string {
  if (!role) return "#8b93a1";
  return PROJECT_ROLE_META[role as ProjectRole]?.color ?? "#8b93a1";
}

export function presenceModeLabel(mode?: ProjectPresenceUser["mode"] | null): string {
  if (mode === "edit") return t("редактирует");
  if (mode === "comment") return t("комментирует");
  return t("читает");
}

export function presenceModeColor(mode?: ProjectPresenceUser["mode"] | null): string {
  if (mode === "edit") return "var(--green)";
  if (mode === "comment") return "var(--accent)";
  return "var(--text-dim)";
}

export function statusLabel(status?: ProjectStatus | string | null): string {
  return localized(PROJECT_STATUS_META[status as ProjectStatus]?.label) ?? status ?? "";
}

export function statusColor(status?: ProjectStatus | string | null): string {
  return PROJECT_STATUS_META[status as ProjectStatus]?.color ?? "#8b93a1";
}

export function formatLabel(format?: ProjectFormat | string | null): string {
  return localized(PROJECT_FORMAT_META[format as ProjectFormat]?.label) ?? format ?? "";
}

export function formatAccent(format?: ProjectFormat | string | null): string {
  return PROJECT_FORMAT_META[format as ProjectFormat]?.accent ?? "#f0a93b";
}

export function characterRoleLabel(role?: CharacterRole | string | null): string {
  return localized(CHARACTER_ROLE_META[role as CharacterRole]?.label) ?? role ?? "";
}

export function characterRoleColor(role?: CharacterRole | string | null): string {
  return CHARACTER_ROLE_META[role as CharacterRole]?.color ?? "#8b93a1";
}

export function structureStatusLabel(status?: StructureStatus | string | null): string {
  return localized(STRUCTURE_STATUS_META[status as StructureStatus]?.label) ?? status ?? "";
}

export function structureStatusColor(status?: StructureStatus | string | null): string {
  return STRUCTURE_STATUS_META[status as StructureStatus]?.color ?? "#8b93a1";
}

export function productionStageLabel(stage?: ProductionStage | string | null): string {
  return localized(PRODUCTION_STAGE_META[stage as ProductionStage]?.label) ?? stage ?? "";
}

export function productionStatusLabel(status?: ProductionStatus | string | null): string {
  return localized(PRODUCTION_STATUS_META[status as ProductionStatus]?.label) ?? status ?? "";
}

export function revisionLabel(color?: RevisionColor | string | null): string {
  return localized(REVISION_COLORS[color as RevisionColor]?.label) ?? t("Чистовик");
}

export function errMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return t("Что-то пошло не так");
}

export function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 5) return t("Доброй ночи");
  if (hour < 12) return t("Доброе утро");
  if (hour < 18) return t("Добрый день");
  return t("Добрый вечер");
}

export function episodesLabel(format?: string | null): string {
  if (format === "series" || format === "audioplay") return t("серии");
  if (format === "comic") return t("страницы");
  return t("сцены");
}

export function pagesLabel(n: number): string {
  return formatPages(n);
}

export function initialsColor(name: string, fallback = "#f0a93b"): { initials: string; color: string } {
  return { initials: initials(name), color: fallback };
}

export function fileSize(bytes: number): string {
  const locale = localeTag();
  const number = (value: number, digits: number) =>
    new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
  if (bytes < 1024) return `${formatNumber(bytes)} ${t("Б")}`;
  if (bytes < 1024 * 1024) return `${number(bytes / 1024, 1)} ${t("КБ")}`;
  if (bytes < 1024 * 1024 * 1024) return `${number(bytes / 1024 / 1024, 1)} ${t("МБ")}`;
  return `${number(bytes / 1024 / 1024 / 1024, 2)} ${t("ГБ")}`;
}

export function timeAgo(value?: string | null): string {
  return relativeTime(value);
}

export function toDateInput(value?: string | null): string {
  if (!value) return "";
  return new Date(value).toISOString().slice(0, 10);
}

export function initialsOf(name: string): string {
  return initials(name);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function randomCoverPattern(seed: string): "signal" | "grid" | "arc" | "noise" {
  const patterns = ["signal", "grid", "arc", "noise"] as const;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return patterns[Math.abs(hash) % patterns.length];
}

export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last > 1 && last < 5) return few;
  if (last === 1) return one;
  return many;
}
